/**
 * 01 · El mes en siete números: valor, chips vs BGT y vs mes anterior, bullet
 * (real contra plan) y sparkline enero → último mes cerrado. La séptima es
 * Payroll (Cogs + G&A + Investment): antes no vivía en ninguna pantalla.
 */

import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { isFavorable, type PnlBase, type PnlKpi, type PnlPeriod } from '../../../../../shared/finance-pnl';
import { C, type PnlFmt } from './pnl-format';
import { PnlNote, SectionHead } from './pnl-ui';

const LABEL_KEY: Record<PnlKpi['key'], string> = {
  revenue: 'budget_control.lines.revenue',
  opex: 'budget_control.lines.opex',
  grossProfit: 'budget_control.lines.grossProfit',
  grossMargin: 'budget_control.lines.grossMargin',
  ebitda: 'budget_control.lines.ebitda',
  ebitdaPct: 'budget_control.lines.ebitdaPct',
  payroll: 'budget_control.lines.payroll',
};

function sparkPath(vals: number[], w: number, h: number): string {
  if (vals.length < 2) return '';
  const mn = Math.min(...vals);
  let mx = Math.max(...vals);
  if (mx === mn) mx = mn + 1;
  const pad = 3;
  const ih = h - pad * 2;
  return vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * w;
      const y = pad + ih - ((v - mn) / (mx - mn)) * ih;
      return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

function Chip({ label, text, favorable, dim }: { label: string; text: string; favorable: boolean; dim: boolean }) {
  return (
    <span
      className={cn('whitespace-nowrap rounded-md border px-1.5 py-px font-mono text-[11.5px] font-semibold', dim && 'opacity-50')}
      style={{
        background: favorable ? C.favSoft : C.unfavSoft,
        color: favorable ? C.fav : C.unfav,
        borderColor: favorable ? C.fav : C.unfav,
      }}
    >
      <span className="mr-1 font-sans font-medium opacity-75">{label}</span>
      {text}
    </span>
  );
}

function Bullet({ value, plan, favorable }: { value: number; plan: number | null; favorable: boolean }) {
  const bw = 200;
  const bh = 16;
  const points = plan === null ? [0, value] : [0, value, plan];
  let lo = Math.min(...points);
  let hi = Math.max(...points);
  let sp = hi - lo || 1;
  const p2 = sp * 0.06;
  lo -= p2;
  hi += p2;
  sp = hi - lo;
  const X = (v: number) => ((v - lo) / sp) * bw;
  const zx = X(0);
  const ax = X(value);
  const px = plan === null ? null : X(plan);
  return (
    <svg viewBox={`0 0 ${bw} ${bh}`} width="100%" height={bh} aria-hidden="true">
      <rect x={0} y={5} width={bw} height={6} rx={3} style={{ fill: C.surface2 }} />
      {px !== null && (
        <rect x={Math.min(zx, px)} y={3} width={Math.max(2, Math.abs(px - zx))} height={10} rx={2} style={{ fill: C.hairStrong, opacity: 0.85 }} />
      )}
      <rect x={Math.min(zx, ax)} y={6} width={Math.max(2, Math.abs(ax - zx))} height={4} rx={2} style={{ fill: favorable ? C.fav : C.unfav }} />
      {px !== null && <rect x={px - 1} y={1} width={2} height={14} rx={1} style={{ fill: C.ink2 }} />}
    </svg>
  );
}

export function PnlKpis({
  kpis,
  base,
  period,
  month,
  closedMonth,
  fmt,
  notes,
}: {
  kpis: PnlKpi[];
  base: PnlBase;
  period: PnlPeriod;
  month: number;
  closedMonth: number;
  fmt: PnlFmt;
  notes: boolean;
}) {
  const { t } = useTranslation();
  const from = fmt.monthShort(1);
  const to = fmt.monthShort(closedMonth);
  return (
    <section>
      <SectionHead
        num="01"
        title={period === 'month' ? t('budget_control.kpis.title_month') : t('budget_control.kpis.title_ytd')}
        sub={t('budget_control.kpis.subtitle', { from, to })}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {kpis.map((r) => {
          const dPlan = r.budget === null ? null : r.value - r.budget;
          const favPlan = dPlan === null ? true : isFavorable(r.up, dPlan);
          const dPrev = r.prev === null ? null : r.value - r.prev;
          const favPrev = dPrev === null ? true : isFavorable(r.up, dPrev);
          const pctPlan = r.pp ? dPlan : r.budget ? ((r.value - r.budget) / Math.abs(r.budget)) * 100 : null;
          const pctPrev = r.pp ? dPrev : r.prev ? ((r.value - (r.prev ?? 0)) / Math.abs(r.prev)) * 100 : null;
          const last = r.series[r.series.length - 1] ?? 0;
          const mn = Math.min(...r.series);
          const mx = Math.max(...r.series);
          const ly = 3 + 16 - ((last - mn) / (mx - mn || 1)) * 16;
          return (
            <Card key={r.key} className="flex flex-col gap-2.5 px-4 pb-3 pt-3.5 shadow-xs">
              <div className="text-[11px] font-semibold uppercase tracking-[0.075em] text-muted-foreground">{t(LABEL_KEY[r.key])}</div>
              <div className="font-mono text-[27px] font-semibold leading-none tracking-tight tabular-nums">{r.pp ? fmt.pct(r.value) : fmt.k(r.value)}</div>
              <div className="flex flex-wrap gap-1.5">
                {dPlan !== null && (
                  <Chip label={t('budget_control.kpis.vs_budget')} text={fmt.delta(pctPlan, dPlan, r.pp)} favorable={favPlan} dim={base !== 'budget'} />
                )}
                {dPrev !== null && (
                  <Chip label={fmt.monthShort(month - 1)} text={fmt.delta(pctPrev, dPrev, r.pp)} favorable={favPrev} dim={base !== 'prev'} />
                )}
              </div>
              <Bullet value={r.value} plan={r.budget} favorable={favPlan} />
              <div className="mt-px flex items-center justify-between gap-2 border-t border-border pt-2">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {t('budget_control.kpis.series', { from, to })}
                </span>
                <svg viewBox="0 0 120 22" width="120" height="22" aria-hidden="true">
                  <path d={sparkPath(r.series, 120, 22)} fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: C.ink3 }} />
                  <circle cx="120" cy={ly.toFixed(1)} r="2.6" style={{ fill: favPlan ? C.fav : C.unfav }} />
                </svg>
              </div>
            </Card>
          );
        })}
      </div>
      <PnlNote
        show={notes}
        items={[
          { tag: 'sign', title: t('budget_control.notes.kpis.sign_title'), text: t('budget_control.notes.kpis.sign_text') },
          { tag: 'color', title: t('budget_control.notes.kpis.color_title'), text: t('budget_control.notes.kpis.color_text') },
          { tag: 'source', title: t('budget_control.notes.kpis.source_title'), text: t('budget_control.notes.kpis.source_text') },
          { tag: 'new', title: t('budget_control.notes.kpis.payroll_title'), text: t('budget_control.notes.kpis.payroll_text') },
        ]}
      />
    </section>
  );
}
